import React, { type SVGProps, useEffect, useState } from 'react'
import { Box, Chip, Menu, MenuItem, ListItemIcon, ListItemText } from '@mui/material'

import { type CardActivityWithId } from '@/models/card-activity/card-activity.model'

interface MCGradingMethodSelectorProps {
  card: CardActivityWithId
  updateCard: (id: string, field: keyof CardActivityWithId, value: Partial<CardActivityWithId>) => void
}

function MCGradingMethodSelector({ card, updateCard }: MCGradingMethodSelectorProps): JSX.Element {
  const [selectedMethod, setSelectedMethod] = useState<'single' | 'multi'>(card?.MCQType ?? 'single')

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)

  const gradingOptions = [
    {
      chipLabel: 'Single Select',
      menuLabel: 'Single Select',
      icon: <SingleSelectIcon />,
      value: 'single' as const,
    },
    {
      chipLabel: 'Multi Select',
      menuLabel: 'Multi Select',
      icon: <MultiSelectIcon />,
      value: 'multi' as const,
    },
  ]

  const handleOpen = (event: React.MouseEvent<HTMLDivElement>) => {
    event.preventDefault()
    event.stopPropagation()
    setAnchorEl(event.currentTarget)
  }

  const handleClose = () => {
    setAnchorEl(null)
  }

  const handleSelect = (value: CardActivityWithId['MCQType']) => {
    updateCard(card.id, 'MCQType', value)
    setSelectedMethod(value)
    handleClose()
  }

  useEffect(() => {
    setSelectedMethod(card?.MCQType)
  }, [card?.MCQType])

  const currentChip = gradingOptions.find(opt => opt.value === selectedMethod)

  return (
    <Box alignItems="center" display="flex" gap={2}>
      <Chip
        icon={currentChip?.icon}
        label={currentChip?.chipLabel}
        sx={{
          cursor: 'pointer',
          fontWeight: 500,
          padding: '8px 16px 8px 8px',
          gap: '8px',
          borderRadius: '16px',
          background: '#F2F4F7',
          height: 42,
          '& .MuiChip-label': { fontSize: 14, p: 0 },
          '& .MuiChip-icon': { m: 0 },
        }}
        onClick={handleOpen}
      />

      <Menu
        anchorEl={anchorEl}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        open={Boolean(anchorEl)}
        sx={{
          '& .MuiPaper-root': {
            borderRadius: '16px',
            border: '1px solid #EAECF0',
            background: '#FFF',
            boxShadow: '0px 12px 16px -4px rgba(16, 24, 40, 0.08), 0px 4px 6px -2px rgba(16, 24, 40, 0.03)',
          },
        }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        onClose={handleClose}
      >
        {gradingOptions.map(option => (
          <MenuItem
            key={option.value}
            selected={option.value === selectedMethod}
            onClick={e => {
              e.preventDefault()
              e.stopPropagation()
              handleSelect(option.value)
            }}
          >
            {/* <ListItemIcon>{option.icon}</ListItemIcon> */}
            <ListItemText primary={option.menuLabel} />
            <ListItemIcon sx={{ ml: 1 }}>
              {/* {option.value === selectedMethod && <CheckRoundedIcon color="primary" fontSize="small" />} */}
            </ListItemIcon>
          </MenuItem>
        ))}
      </Menu>
    </Box>
  )
}

export default MCGradingMethodSelector

function GradingMethodIconRubric(props: SVGProps<SVGSVGElement>) {
  return (
    <svg fill="none" height={30} viewBox="0 0 30 30" width={30} xmlns="http://www.w3.org/2000/svg" {...props}>
      <path
        d="M10.6667 1.13333C5.40155 1.13333 1.13333 5.40155 1.13333 10.6667V19.3333C1.13333 24.5984 5.40155 28.8667 10.6667 28.8667H19.3333C24.5984 28.8667 28.8667 24.5984 28.8667 19.3333V10.6667C28.8667 5.40155 24.5984 1.13333 19.3333 1.13333H10.6667Z"
        fill="#7A5AF8"
      />
      <path
        d="M10.6667 1.13333C5.40155 1.13333 1.13333 5.40155 1.13333 10.6667V19.3333C1.13333 24.5984 5.40155 28.8667 10.6667 28.8667H19.3333C24.5984 28.8667 28.8667 24.5984 28.8667 19.3333V10.6667C28.8667 5.40155 24.5984 1.13333 19.3333 1.13333H10.6667Z"
        stroke="#F4F3FF"
        strokeWidth={1.73333}
      />
      <path
        d="M17.4074 10.1842C17.9671 10.1842 18.247 10.1842 18.4766 10.2457C19.0997 10.4126 19.5863 10.8993 19.7533 11.5224C19.8148 11.752 19.8148 12.0319 19.8148 12.5916V18.1286C19.8148 19.1398 19.8148 19.6454 19.618 20.0317C19.4449 20.3714 19.1687 20.6476 18.829 20.8207C18.4427 21.0175 17.9371 21.0175 16.9259 21.0175H13.0741C12.0629 21.0175 11.5573 21.0175 11.171 20.8207C10.8313 20.6476 10.5551 20.3714 10.382 20.0317C10.1852 19.6454 10.1852 19.1398 10.1852 18.1286V12.5916C10.1852 12.0319 10.1852 11.752 10.2467 11.5224C10.4137 10.8993 10.9003 10.4126 11.5234 10.2457C11.753 10.1842 12.0329 10.1842 12.5926 10.1842M13.1944 16.8045L14.3981 18.0082L17.1065 15.2999M13.5556 11.3879H16.4444C16.7815 11.3879 16.95 11.3879 17.0788 11.3223C17.192 11.2646 17.2841 11.1725 17.3418 11.0593C17.4074 10.9305 17.4074 10.762 17.4074 10.4249V9.94343C17.4074 9.60636 17.4074 9.43783 17.3418 9.30909C17.2841 9.19584 17.192 9.10377 17.0788 9.04607C16.95 8.98047 16.7815 8.98047 16.4444 8.98047H13.5556C13.2185 8.98047 13.0499 8.98047 12.9212 9.04607C12.808 9.10377 12.7159 9.19584 12.6582 9.30909C12.5926 9.43783 12.5926 9.60636 12.5926 9.94343V10.4249C12.5926 10.762 12.5926 10.9305 12.6582 11.0593C12.7159 11.1725 12.808 11.2646 12.9212 11.3223C13.0499 11.3879 13.2185 11.3879 13.5556 11.3879Z"
        stroke="white"
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={1.2037}
      />
    </svg>
  )
}

function GradingMethodIconPassFail(props: SVGProps<SVGSVGElement>) {
  return (
    <svg fill="none" height={30} viewBox="0 0 30 30" width={30} xmlns="http://www.w3.org/2000/svg" {...props}>
      <path
        d="M10.6667 1.13333C5.40155 1.13333 1.13333 5.40155 1.13333 10.6667V19.3333C1.13333 24.5984 5.40155 28.8667 10.6667 28.8667H19.3333C24.5984 28.8667 28.8667 24.5984 28.8667 19.3333V10.6667C28.8667 5.40155 24.5984 1.13333 19.3333 1.13333H10.6667Z"
        fill="#66C61C"
      />
      <path
        d="M10.6667 1.13333C5.40155 1.13333 1.13333 5.40155 1.13333 10.6667V19.3333C1.13333 24.5984 5.40155 28.8667 10.6667 28.8667H19.3333C24.5984 28.8667 28.8667 24.5984 28.8667 19.3333V10.6667C28.8667 5.40155 24.5984 1.13333 19.3333 1.13333H10.6667Z"
        stroke="#E4FBCC"
        strokeWidth={1.73333}
      />
      <path
        d="M11.9907 16.8589V21.0175L14.8212 19.8853C14.8872 19.8589 14.9202 19.8457 14.9543 19.8405C14.9846 19.8358 15.0154 19.8358 15.0456 19.8405C15.0797 19.8457 15.1127 19.8589 15.1788 19.8853L18.0092 21.0175V16.8589M19.5139 13.4944C19.5139 15.9873 17.4929 18.0082 15 18.0082C12.507 18.0082 10.4861 15.9873 10.4861 13.4944C10.4861 11.0014 12.507 8.98047 15 8.98047C17.4929 8.98047 19.5139 11.0014 19.5139 13.4944Z"
        stroke="white"
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={1.2037}
      />
    </svg>
  )
}

function MultiSelectIcon(props: SVGProps<SVGSVGElement>) {
  return (
    <svg fill="none" height={30} viewBox="0 0 30 30" width={30} xmlns="http://www.w3.org/2000/svg" {...props}>
      <path
        d="M10.6667 1.13333C5.40155 1.13333 1.13333 5.40155 1.13333 10.6667V19.3333C1.13333 24.5984 5.40155 28.8667 10.6667 28.8667H19.3333C24.5984 28.8667 28.8667 24.5984 28.8667 19.3333V10.6667C28.8667 5.40155 24.5984 1.13333 19.3333 1.13333H10.6667Z"
        fill="#86CB3C"
      />
      <path
        d="M10.6667 1.13333C5.40155 1.13333 1.13333 5.40155 1.13333 10.6667V19.3333C1.13333 24.5984 5.40155 28.8667 10.6667 28.8667H19.3333C24.5984 28.8667 28.8667 24.5984 28.8667 19.3333V10.6667C28.8667 5.40155 24.5984 1.13333 19.3333 1.13333H10.6667Z"
        stroke="#F5FBEE"
        strokeWidth={1.73333}
      />
      <path
        d="M12.2904 14.9987L14.0959 16.8043L17.707 13.1931M12.4709 20.4154H17.5265C18.5377 20.4154 19.0433 20.4154 19.4295 20.2186C19.7693 20.0455 20.0455 19.7693 20.2186 19.4295C20.4154 19.0433 20.4154 18.5377 20.4154 17.5265V12.4709C20.4154 11.4597 20.4154 10.9541 20.2186 10.5679C20.0455 10.2281 19.7693 9.95193 19.4295 9.77883C19.0433 9.58203 18.5377 9.58203 17.5265 9.58203H12.4709C11.4597 9.58203 10.9541 9.58203 10.5679 9.77883C10.2281 9.95193 9.95193 10.2281 9.77883 10.5679C9.58203 10.9541 9.58203 11.4597 9.58203 12.4709V17.5265C9.58203 18.5377 9.58203 19.0433 9.77883 19.4295C9.95193 19.7693 10.2281 20.0455 10.5679 20.2186C10.9541 20.4154 11.4597 20.4154 12.4709 20.4154Z"
        stroke="white"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  )
}

function SingleSelectIcon(props: SVGProps<SVGSVGElement>) {
  return (
    <svg fill="none" height={30} viewBox="0 0 30 30" width={30} xmlns="http://www.w3.org/2000/svg" {...props}>
      <path
        d="M10.6667 1.13333C5.40155 1.13333 1.13333 5.40155 1.13333 10.6667V19.3333C1.13333 24.5984 5.40155 28.8667 10.6667 28.8667H19.3333C24.5984 28.8667 28.8667 24.5984 28.8667 19.3333V10.6667C28.8667 5.40155 24.5984 1.13333 19.3333 1.13333H10.6667Z"
        fill="#86CB3C"
      />
      <path
        d="M10.6667 1.13333C5.40155 1.13333 1.13333 5.40155 1.13333 10.6667V19.3333C1.13333 24.5984 5.40155 28.8667 10.6667 28.8667H19.3333C24.5984 28.8667 28.8667 24.5984 28.8667 19.3333V10.6667C28.8667 5.40155 24.5984 1.13333 19.3333 1.13333H10.6667Z"
        stroke="#F5FBEE"
        strokeWidth={1.73333}
      />
      <path
        d="M14.999 21.0175C18.3229 21.0175 21.0175 18.3229 21.0175 14.999C21.0175 11.6751 18.3229 8.98047 14.999 8.98047C11.6751 8.98047 8.98047 11.6751 8.98047 14.999C8.98047 18.3229 11.6751 21.0175 14.999 21.0175Z"
        stroke="white"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M14.999 16.8045C15.9962 16.8045 16.8045 15.9962 16.8045 14.999C16.8045 14.0018 15.9962 13.1934 14.999 13.1934C14.0018 13.1934 13.1934 14.0018 13.1934 14.999C13.1934 15.9962 14.0018 16.8045 14.999 16.8045Z"
        stroke="white"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  )
}
